import { FC } from 'react';
import { SEOBase, SEOBaseProps } from './SEOBase';

export { SITE_URL, SITE_LOGO } from './SEOBase';

// Maximum length for SEO elements
const MAX_TITLE_LENGTH = 65;
const MAX_DESCRIPTION_LENGTH = 120;

interface SEOProps extends Omit<SEOBaseProps, 'structuredData' | 'children'> {
  type?: 'website' | 'article' | 'profile' | 'food.restaurant' | 'review' | 'product' | 'product.review';
  restaurantData?: {
    name?: string;
    address?: string;
    city?: string;
    region?: string;
    postalCode?: string;
    country?: string;
    phone?: string;
    priceRange?: string;
    cuisine?: string[];
    rating?: number;
    reviewCount?: number;
    latitude?: number;
    longitude?: number;
  };
  reviewData?: {
    reviewRating: number;
    reviewBody?: string;
    datePublished?: string;
    author?: string;
    itemReviewed?: {
      type: 'Restaurant' | 'Product' | 'LocalBusiness' | 'Organization';
      name: string;
      image?: string;
      description?: string;
      url?: string;
      address: string;
    };
  };
  aggregateRatingData?: {
    ratingValue: number;
    reviewCount: number;
    bestRating?: number;
    worstRating?: number;
    itemReviewed?: {
      type: 'Restaurant' | 'Product' | 'LocalBusiness' | 'Organization';
      name: string;
      image?: string;
      description?: string;
      url?: string;
    };
  };
  personData?: {
    name: string;
    image?: string;
    description?: string;
    username?: string;
    url?: string;
    jobTitle?: string;
    worksFor?: string;
    sameAs?: string[];
    reviewCount?: number;
    memberSince?: string;
  };
  productData?: {
    name: string;
    image: string;
    description?: string;
    brand?: string;
    offers?: {
      price?: number | string;
      priceCurrency?: string;
      availability?: string;
    };
    aggregateRating?: {
      ratingValue: number;
      reviewCount: number;
      bestRating?: number;
      worstRating?: number;
    };
    review?: {
      reviewRating: number;
      reviewBody?: string;
      datePublished?: string;
      author?: string;
    };
  };
  popularFoodsData?: Array<{
    id: string | number;
    name: string;
    description?: string | null;
    mainImageId?: string | null;
    reviewOverallRating?: number;
    reviewCount?: number;
  }>;
}

export const SEO: FC<SEOProps> = ({
  type = 'website',
  restaurantData,
  reviewData,
  aggregateRatingData,
  personData,
  productData,
  popularFoodsData,
  ...baseProps
}) => {
  // Ensure title length is within SEO limits
  const truncatedTitle = baseProps.title && baseProps.title.length > MAX_TITLE_LENGTH
    ? baseProps.title.substring(0, MAX_TITLE_LENGTH - 3) + '...'
    : baseProps.title;

  // Ensure description length is within SEO limits
  const truncatedDescription = baseProps.description && baseProps.description.length > MAX_DESCRIPTION_LENGTH
    ? baseProps.description.substring(0, MAX_DESCRIPTION_LENGTH - 3) + '...'
    : baseProps.description;

  // Generate JSON-LD structured data
  const generateStructuredData = () => {
    const structuredDataArray = [];

    // Simple function to strip HTML tags
    const stripHtml = (html: string | null | undefined): string | undefined => {
      if (!html) return undefined;
      const strippedText = html.replace(/<\/?[^>]+(>|$)/g, "").replace(/&[a-z]+;/g, " ").trim();
      // Limit to 500 characters for JSON-LD compatibility
      return strippedText.length > 500 ? strippedText.substring(0, 500) + '...' : strippedText;
    };

    // For restaurant pages, consolidate all data into a single structured data object
    if (type === 'food.restaurant' && restaurantData) {
      // Create a comprehensive restaurant structured data object
      const restaurantStructuredData = {
        '@context': 'https://schema.org',
        '@type': 'Restaurant',
        name: restaurantData.name || truncatedTitle,
        description: truncatedDescription,
        // Ensure image is present - required for rich results
        image: baseProps.image || 'https://bitecritic.com/images/hero-bg.webp',
        // Ensure URL is present - required for rich results
        url: baseProps.canonicalUrl || (typeof window !== 'undefined' ? window.location.href : ''),
        ...(restaurantData.address && {
          address: {
            '@type': 'PostalAddress',
            streetAddress: restaurantData.address,
            addressLocality: restaurantData.city || '',
            addressRegion: restaurantData.region || '',
            postalCode: restaurantData.postalCode || '',
            addressCountry: restaurantData.country || '',
          }
        }),
        ...(restaurantData.phone && { telephone: restaurantData.phone }),
        ...(restaurantData.priceRange && { priceRange: restaurantData.priceRange }),
        ...(restaurantData.cuisine && { servesCuisine: restaurantData.cuisine }),
        ...(restaurantData.rating && restaurantData.reviewCount && restaurantData.reviewCount > 0 && {
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: restaurantData.rating,
            reviewCount: restaurantData.reviewCount,
            bestRating: 10,
            worstRating: 0.01,
          },
        }),
        ...(restaurantData.latitude && restaurantData.longitude && {
          geo: {
            '@type': 'GeoCoordinates',
            latitude: restaurantData.latitude,
            longitude: restaurantData.longitude,
          },
        }),
        // Add popular foods nested under hasMenu -> Menu -> hasMenuItem
        ...(popularFoodsData && popularFoodsData.length > 0 && {
          hasMenu: {
            '@type': 'Menu',
            name: `Popular items at ${restaurantData.name || 'the restaurant'}`,
            hasMenuItem: popularFoodsData.map(food => ({
              '@type': 'MenuItem',
              name: food.name,
              ...(food.description && { description: stripHtml(food.description) }),
              ...(food.mainImageId && { image: food.mainImageId }),
              // Add offers - required for product rich results if menu item is treated as such
              offers: {
                '@type': 'Offer',
                price: '0', // Replace with actual price if available
                priceCurrency: 'EUR', // Use appropriate currency
                availability: 'https://schema.org/InStock'
              }
            }))
          }
        })
      };

      structuredDataArray.push(restaurantStructuredData);

      // If we have aggregateRatingData with itemReviewed, we'll add it to the restaurant data
      // instead of creating a separate object
      if (aggregateRatingData && aggregateRatingData.reviewCount > 0 && aggregateRatingData.itemReviewed) {
        // We've already included the aggregateRating in the restaurant data if available
        // No need to add a separate object
      }
    }
    // For profile pages, add Person structured data
    else if (type === 'profile' && personData) {
      const personStructuredData = {
        '@context': 'https://schema.org',
        '@type': 'Person',
        name: personData.name,
        ...(personData.image && { image: personData.image }),
        ...(personData.description && { description: personData.description }),
        ...(personData.url && { url: personData.url }),
        ...(personData.jobTitle && { jobTitle: personData.jobTitle }),
        ...(personData.worksFor && {
          worksFor: {
            '@type': 'Organization',
            name: personData.worksFor
          }
        }),
        ...(personData.sameAs && { sameAs: personData.sameAs }),
        ...(personData.username && { alternateName: personData.username }),
        ...(personData.memberSince && {
          memberOf: {
            '@type': 'Organization',
            name: 'BiteCritic',
            startDate: personData.memberSince
          }
        }),
      };

      structuredDataArray.push(personStructuredData);
    }
    // For non-restaurant pages or when restaurantData is not provided
    else {
      // Base structured data - only add if productData is not provided and type is product
      if (!(type === 'product' && productData)) {
        const baseData = {
          '@context': 'https://schema.org',
          '@type': type === 'food.restaurant' ? 'Restaurant' :
            type === 'profile' ? 'Person' :
              type === 'review' ? 'Review' :
                type === 'product' ? 'Product' : 'WebPage',
          name: truncatedTitle,
          description: truncatedDescription,
          ...(baseProps.image && { image: baseProps.image }),
          ...(baseProps.canonicalUrl && { url: baseProps.canonicalUrl }),
        };

        structuredDataArray.push(baseData);
      }

      // Add restaurant structured data if provided
      if (restaurantData && type === 'food.restaurant') {
        const restaurantStructuredData = {
          '@context': 'https://schema.org',
          '@type': 'Restaurant',
          name: restaurantData.name,
          address: {
            '@type': 'PostalAddress',
            streetAddress: restaurantData.address,
            addressLocality: restaurantData.city,
            addressRegion: restaurantData.region,
            postalCode: restaurantData.postalCode,
            addressCountry: restaurantData.country,
          },
          ...(restaurantData.phone && { telephone: restaurantData.phone }),
          ...(restaurantData.priceRange && { priceRange: restaurantData.priceRange }),
          ...(restaurantData.cuisine && { servesCuisine: restaurantData.cuisine }),
          ...(restaurantData.rating && restaurantData.reviewCount && restaurantData.reviewCount > 0 && {
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: restaurantData.rating,
              reviewCount: restaurantData.reviewCount,
              bestRating: 10,
              worstRating: 0.01,
            },
          }),
          ...(restaurantData.latitude && restaurantData.longitude && {
            geo: {
              '@type': 'GeoCoordinates',
              latitude: restaurantData.latitude,
              longitude: restaurantData.longitude,
            },
          }),
        };

        structuredDataArray.push(restaurantStructuredData);
      }

      // Add aggregate rating structured data if provided
      if (aggregateRatingData && aggregateRatingData.reviewCount > 0 && type !== 'food.restaurant') {
        const aggregateRatingStructuredData = {
          '@context': 'https://schema.org',
          '@type': 'AggregateRating',
          ratingValue: aggregateRatingData.ratingValue,
          reviewCount: aggregateRatingData.reviewCount,
          bestRating: aggregateRatingData.bestRating || 10,
          worstRating: aggregateRatingData.worstRating || 0,
          ...(aggregateRatingData.itemReviewed && {
            itemReviewed: {
              '@type': aggregateRatingData.itemReviewed.type,
              name: aggregateRatingData.itemReviewed.name,
              ...(aggregateRatingData.itemReviewed.image && { image: aggregateRatingData.itemReviewed.image }),
              ...(aggregateRatingData.itemReviewed.description && { description: aggregateRatingData.itemReviewed.description }),
              ...(aggregateRatingData.itemReviewed.url && { url: aggregateRatingData.itemReviewed.url }),
            },
          }),
        };

        structuredDataArray.push(aggregateRatingStructuredData);
      }
    }

    // Add review structured data if provided
    if (reviewData && type === 'review' && reviewData.reviewRating > 0) {
      const reviewStructuredData = {
        '@context': 'https://schema.org',
        '@type': 'Review',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: reviewData.reviewRating,
          bestRating: 10,
          worstRating: 0.01,
        },
        ...(reviewData.reviewBody && { reviewBody: reviewData.reviewBody }),
        ...(reviewData.datePublished && { datePublished: reviewData.datePublished }),
        ...(reviewData.author && { author: { '@type': 'Person', name: reviewData.author } }),
        ...(reviewData.itemReviewed && {
          itemReviewed: {
            '@type': reviewData.itemReviewed.type,
            name: reviewData.itemReviewed.name,
            ...(reviewData.itemReviewed.image && { image: reviewData.itemReviewed.image }),
            ...(reviewData.itemReviewed.description && { description: reviewData.itemReviewed.description }),
            ...(reviewData.itemReviewed.url && { url: reviewData.itemReviewed.url }),
            ...(reviewData.itemReviewed.address && {
              address: {
                '@type': 'PostalAddress',
                streetAddress: reviewData.itemReviewed.address
              }
            }),
          },
        }),
      };

      structuredDataArray.push(reviewStructuredData);
    }

    // Add product structured data if provided
    if (productData) {
      // Ensure at least one of offers, review, or aggregateRating is present
      const hasRequiredProperty =
        (productData.offers && Object.keys(productData.offers).length > 0) ||
        (productData.review && Object.keys(productData.review).length > 0) ||
        (productData.aggregateRating && productData.aggregateRating.reviewCount > 0 && Object.keys(productData.aggregateRating).length > 0);

      // If no required property is present, add a dummy offer
      const dummyOffer = !hasRequiredProperty ? {
        offers: {
          '@type': 'Offer',
          availability: 'https://schema.org/InStock',
          price: '0',
          priceCurrency: 'EUR'
        }
      } : {};

      const productStructuredData = {
        '@context': 'https://schema.org/',
        '@type': 'Product',
        name: productData.name,
        image: productData.image,
        description: productData.description || truncatedDescription,
        ...(productData.brand && { brand: { '@type': 'Brand', name: productData.brand } }),
        ...(productData.offers && {
          offers: {
            '@type': 'Offer',
            price: productData.offers.price || '0',
            priceCurrency: productData.offers.priceCurrency || 'EUR',
            availability: productData.offers.availability || 'https://schema.org/InStock',
          }
        }),
        ...(productData.aggregateRating && productData.aggregateRating.reviewCount > 0 && {
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: productData.aggregateRating.ratingValue,
            reviewCount: productData.aggregateRating.reviewCount,
            bestRating: productData.aggregateRating.bestRating || 10,
            worstRating: productData.aggregateRating.worstRating || 0,
          }
        }),
        ...(productData.review && productData.review.reviewRating > 0 && {
          review: {
            '@type': 'Review',
            reviewRating: {
              '@type': 'Rating',
              ratingValue: productData.review.reviewRating,
              bestRating: 10,
              worstRating: 0.01,
            },
            reviewBody: productData.review.reviewBody,
            datePublished: productData.review.datePublished,
            author: {
              '@type': 'Person',
              name: productData.review.author || 'Anonymous',
            }
          }
        }),
        ...dummyOffer
      };

      structuredDataArray.push(productStructuredData);
    }

    return structuredDataArray.length > 1 ? JSON.stringify(structuredDataArray) : JSON.stringify(structuredDataArray[0]);
  };

  // Generate additional meta tags for specific types
  const additionalMetaTags = (
    <>
      {/* Additional Open Graph tags for reviews */}
      {(type === 'review' || type === 'product.review') && (
        <>
          <meta property="og:rating" content={reviewData?.reviewRating?.toString() || productData?.review?.reviewRating?.toString() || '0'} />
          <meta property="og:rating:scale" content="10" />
          {reviewData?.author && <meta property="og:author" content={reviewData.author} />}
          {productData?.review?.author && <meta property="og:author" content={productData.review.author} />}
          {reviewData?.itemReviewed?.name && <meta property="og:reviewed:name" content={reviewData.itemReviewed.name} />}
          {productData?.name && <meta property="og:reviewed:name" content={productData.name} />}
          {reviewData?.itemReviewed?.type && <meta property="og:reviewed:type" content={reviewData.itemReviewed.type.toLowerCase()} />}
          {productData?.brand && <meta property="og:reviewed:brand" content={productData.brand} />}
          {reviewData?.itemReviewed?.image && <meta property="og:reviewed:image" content={reviewData.itemReviewed.image} />}
          {reviewData?.datePublished && <meta property="og:published_time" content={reviewData.datePublished} />}
        </>
      )}

      {/* Additional Meta Tags for Food/Restaurant */}
      {type === 'food.restaurant' && restaurantData && (
        <>
          <meta property="restaurant:contact_info:website" content={baseProps.canonicalUrl || ''} />
          <meta property="restaurant:contact_info:street_address" content={restaurantData.address || ''} />
          <meta property="restaurant:contact_info:locality" content={restaurantData.city || ''} />
          <meta property="restaurant:contact_info:region" content={restaurantData.region || ''} />
          <meta property="restaurant:contact_info:postal_code" content={restaurantData.postalCode || ''} />
          <meta property="restaurant:contact_info:country_name" content={restaurantData.country || ''} />
          <meta property="restaurant:contact_info:phone_number" content={restaurantData.phone || ''} />
          {restaurantData.cuisine?.map((cuisine, index) => (
            <meta key={index} property="restaurant:cuisine" content={cuisine} />
          ))}
          {restaurantData.priceRange && (
            <meta property="restaurant:price_range" content={restaurantData.priceRange} />
          )}
        </>
      )}
    </>
  );

  return (
    <SEOBase
      {...baseProps}
      title={truncatedTitle}
      description={truncatedDescription}
      ogType={type}
      structuredData={generateStructuredData()}
    >
      {additionalMetaTags}
    </SEOBase>
  );
};

export default SEO; 